import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SORTING_DIRECTION } from 'src/constants/main';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { BRANCHES_COLUMN_NAME } from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/constants/branches';
import { getBranchesTableColumnsWidth } from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/helpers/branches';
import branchesStore from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/store/branchesStore';
import BranchListRow from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/components/BranchListRow/BranchListRow';
import BranchesLogLink from '~/view/components/Link/BranchesLogLink/BranchesLogLink';
import { SCREENS } from '~/constants/screens';

const BranchesList = observer((): JSX.Element => {
  // --------------- Handlers ---------------

  const onChangeSorting = (sortField: BRANCHES_COLUMN_NAME | undefined, sortDir: SORTING_DIRECTION | undefined) => {
    branchesStore.setFilters({ sortField, sortDir });
  };

  // --------------- Other ---------------

  const {
    is_pos: isPosEnabled,
    is_franchise_unintegrated_branches: isFranchiseBranchesEnabled,
    is_franchise: isFranchiseEnabled,
  } = appStore.settings || {};

  const { pagination, filters } = branchesStore;

  const [mainColumnWidth, secondaryColumnWidth] = useMemo(
    () => getBranchesTableColumnsWidth(isPosEnabled, isFranchiseEnabled, isFranchiseBranchesEnabled),
    []
  );

  // check if list is filtered by deleted status
  const isListOfDeleted = useMemo(() => pagination.data.some((branch) => branch.is_delete), [pagination.data]);

  const isListLoading = (pagination.loading || pagination.refreshing) && !pagination.total;
  const emptyListTitle = branchesStore.isEmptyBranchesList ? 'no_branches_yet' : 'no_branches_found';

  return (
    <div className="d-flex flex-column flex-1 mt-7">
      {!pagination.data.length && !pagination.loading ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-6">
          <img src={formatImagePath(`${Icons.BranchesTree}.svg`)} alt={emptyListTitle} className="image__no-found mb-6" />
          <p className="family-medium text-size-24 color-gray2">{localeStore.t(emptyListTitle)}</p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-row pr-5 pb-4">
            <ColumnTitle<BRANCHES_COLUMN_NAME>
              title="branch_name_column"
              columnName={BRANCHES_COLUMN_NAME.NAME}
              currentField={filters.sortField}
              currentDir={filters.sortDir}
              setFilters={onChangeSorting}
              className={`w-${mainColumnWidth}`}
            />
            {!isListOfDeleted && (
              <>
                <ColumnTitle title="order_type" className={`w-${secondaryColumnWidth}`} />
                <ColumnTitle<BRANCHES_COLUMN_NAME>
                  title="branch_preorder_status_column"
                  columnName={BRANCHES_COLUMN_NAME.PREORDER}
                  currentField={filters.sortField}
                  currentDir={filters.sortDir}
                  setFilters={onChangeSorting}
                  className={`w-${secondaryColumnWidth}`}
                />
                <ColumnTitle title="payment_method" className={`w-${secondaryColumnWidth}`} />
                {isFranchiseBranchesEnabled && isPosEnabled && (
                  <ColumnTitle<BRANCHES_COLUMN_NAME>
                    title="branches_created_in_column"
                    columnName={BRANCHES_COLUMN_NAME.CREATED_IN}
                    currentField={filters.sortField}
                    currentDir={filters.sortDir}
                    setFilters={onChangeSorting}
                    className={`w-${secondaryColumnWidth}`}
                  />
                )}
                {isFranchiseEnabled && (
                  <ColumnTitle<BRANCHES_COLUMN_NAME>
                    title="branches_franchisee_profile_column"
                    columnName={BRANCHES_COLUMN_NAME.FRANCHISEE_PROFILE}
                    currentField={filters.sortField}
                    currentDir={filters.sortDir}
                    setFilters={onChangeSorting}
                    className={`w-${secondaryColumnWidth}`}
                    textClassName="width-min text-start"
                  />
                )}
                <ColumnTitle title="branches_busy_column" className={`w-${secondaryColumnWidth}`} />
                <ColumnTitle title="branches_status_column" className={`w-${secondaryColumnWidth}`} />
              </>
            )}
          </div>
          <ScrollableBlock showScrollbar scrollClassName="d-flex flex-column full-height pr-3" className="overflow-y-scroll mb-3">
            {isListLoading
              ? emptyArray().map((_, index) => (
                  <BranchListRow key={index} mainColumnWidth={mainColumnWidth} secondaryColumnWidth={secondaryColumnWidth} />
                ))
              : pagination.data.map((branch) => (
                  <BranchListRow key={branch.id} branch={branch} mainColumnWidth={mainColumnWidth} secondaryColumnWidth={secondaryColumnWidth} />
                ))}
          </ScrollableBlock>
        </>
      )}
      <BranchesLogLink link={SCREENS.BRANCHES_LOG()} />
      <Pagination pagination={pagination} type="arrow" onChange={branchesStore.fetchBranchesList} />
    </div>
  );
});

export default BranchesList;
